import { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScreenErrorBoundary from './components/common/ScreenErrorBoundary';
import LoginScreen from './components/login/LoginScreen';
import AppShell, { type AppScreen } from './components/common/AppShell';
import ScreenShareHost from './components/common/ScreenShareHost';
import { isOnboarded, hasSeenTutorial, markTutorialSeen, getJSON, setJSON } from './utils/storage';
import { buildTourSteps } from './utils/tourSteps';
import { setThemeId, getThemeId } from './utils/themes';

// Lazy-loaded components (code splitting)
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const PracticeContainer = lazy(() => import('./components/practice/PracticeContainer'));
const ExamContainer = lazy(() => import('./components/exam/ExamContainer'));
const DuelScreen = lazy(() => import('./components/duel/DuelScreen'));
const ExamPractice = lazy(() => import('./components/practice/ExamPractice'));
const AchievementsScreen = lazy(() => import('./components/meta/AchievementsScreen'));
const LeaderboardScreen = lazy(() => import('./components/meta/LeaderboardScreen'));
const GoalsScreen = lazy(() => import('./components/meta/GoalsScreen'));
const SettingsScreen = lazy(() => import('./components/meta/SettingsScreen'));
const LessonTopics = lazy(() => import('./components/lessons/LessonTopics'));
const Lesson = lazy(() => import('./components/lessons/Lesson'));
const LearningPathScreen = lazy(() => import('./components/path/LearningPathScreen'));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow'));
const TutorialModal = lazy(() => import('./components/onboarding/TutorialModal'));
const GuidedTour = lazy(() => import('./components/onboarding/GuidedTour'));
const ParentDashboard = lazy(() => import('./components/parent/ParentDashboard'));
const AdminScreen = lazy(() => import('./components/admin/AdminScreen'));
const TutorDashboard = lazy(() => import('./components/tutor/TutorDashboard'));
const TutorOverview = lazy(() => import('./components/tutor/TutorOverview'));
const PracticeHub = lazy(() => import('./components/practice/PracticeHub'));
const ProgressScreen = lazy(() => import('./components/progress/ProgressScreen'));

function AppMain() {
  const { isLoggedIn, isLoading, student, token } = useAuth();
  // Default landing screen. NOTE: a useState initializer runs ONCE at mount,
  // and `student` is loaded asynchronously by AuthContext — so at mount it's
  // usually still null and this resolves to 'dashboard'. The useEffect below
  // does the real role-based routing once `student` actually arrives.
  const [screen, setScreen] = useState<AppScreen>('dashboard');
  // Last role we routed for. Lets us re-route on an actual role change (e.g.
  // the Settings "switch account type" flow) without fighting the user's
  // own in-app navigation.
  const lastRoleRef = useRef<string | null>(null);
  // Which tutee is open in the overview screen
  const [tutoreeId, setTutoreeId] = useState<string | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [practiceTopicId, setPracticeTopicId] = useState<string | null>(null);
  const [practiceSearch, setPracticeSearch] = useState<string | null>(null);
  // When the kid taps "תרגל את הטעויות" from MistakesReviewModal we route
  // them into Practice with a session filtered to *just those* questions.
  const [practiceMistakeQids, setPracticeMistakeQids] = useState<string[] | null>(null);
  // Show onboarding once for first-time users (after login)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (isLoggedIn && !isOnboarded()) setShowOnboarding(true);
  }, [isLoggedIn]);
  // ── Feature/screen time tracking ──────────────────────────────────
  // Measures how long the user stays on each screen and batches the spans
  // to /telemetry/feature-usage. Flushed on screen change, on a 60s timer,
  // and when the tab is hidden/closed. Powers the owner-analytics
  // "which screens get used + for how long" view.
  const screenEnterRef = useRef<{ feature: string; at: number }>({ feature: 'dashboard', at: Date.now() });
  const usageQueueRef = useRef<{ feature: string; duration_sec: number }[]>([]);
  const flushUsage = useCallback((onUnload = false) => {
    // Close out the current span before flushing.
    const nowMs = Date.now();
    const span = screenEnterRef.current;
    const dur = (nowMs - span.at) / 1000;
    if (dur >= 1) usageQueueRef.current.push({ feature: span.feature, duration_sec: dur });
    screenEnterRef.current = { feature: span.feature, at: nowMs };
    const batch = usageQueueRef.current;
    if (batch.length === 0 || !token) return;
    usageQueueRef.current = [];
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '';
    if (onUnload && apiBase) {
      // During tab-close a normal awaited fetch gets killed — `keepalive`
      // lets the request outlive the page, and unlike sendBeacon it can
      // still carry the Authorization header.
      try {
        void fetch(`${apiBase}/telemetry/feature-usage`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(batch),
        });
      } catch { /* best-effort */ }
      return;
    }
    void import('./utils/api').then((m) => m.recordFeatureUsage(batch, token)).catch(() => {
      // On failure, requeue so the next flush retries (bounded — drop if huge).
      if (usageQueueRef.current.length < 200) usageQueueRef.current.unshift(...batch);
    });
  }, [token]);
  // Record the span each time `screen` changes.
  useEffect(() => {
    const nowMs = Date.now();
    const prev = screenEnterRef.current;
    const dur = (nowMs - prev.at) / 1000;
    if (dur >= 1 && prev.feature !== screen) {
      usageQueueRef.current.push({ feature: prev.feature, duration_sec: dur });
    }
    screenEnterRef.current = { feature: screen, at: nowMs };
  }, [screen]);
  // Periodic flush + flush-on-hide.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => flushUsage(false), 60_000);
    const onHide = () => { if (document.visibilityState === 'hidden') flushUsage(true); };
    window.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', () => flushUsage(true));
    return () => {
      clearInterval(interval);
      window.removeEventListener('visibilitychange', onHide);
      flushUsage(false);
    };
  }, [token, flushUsage]);

  // Role-based landing route. Fires when `student` loads AND whenever the
  // role actually changes (the Settings switch-account flow). The
  // lastRoleRef guard means a plain updateStudent() — which also produces a
  // new student object — won't re-trigger routing if the role is unchanged,
  // so it never fights the user's own navigation.
  useEffect(() => {
    if (!isLoggedIn || !student) { lastRoleRef.current = null; return; }
    const role = student.role;
    if (lastRoleRef.current === role) return;
    const prev = lastRoleRef.current;
    lastRoleRef.current = role;
    if (role === 'teacher') {
      setScreen('tutor_dashboard');
    } else if (prev !== null) {
      // Switched back to student from teacher — land them on the learner home.
      setScreen('dashboard');
    }
  }, [isLoggedIn, student]);
  // Elementary students get the warm "kids-rainbow" theme by default. We
  // only auto-set it once per device (tracked via a small flag) so a kid
  // who later picks a different theme isn't reset on every login.
  useEffect(() => {
    if (!isLoggedIn || !student?.track) return;
    if (!String(student.track).startsWith('el')) return;
    const flagKey = 'bagrut_kids_theme_applied';
    if (getJSON<boolean>(flagKey, false)) return;
    // Only apply if user hasn't already explicitly picked a non-default theme
    if (getThemeId() === 'purple-dawn') {
      setThemeId('kids-rainbow');
    }
    setJSON(flagKey, true);
  }, [isLoggedIn, student?.track]);
  // After the quick onboarding (or for any logged-in user who's onboarded but
  // hasn't seen the full tutorial yet), trigger it once. Settings has a manual
  // re-open button too.
  useEffect(() => {
    if (!isLoggedIn) return;
    if (showOnboarding) return;
    if (!isOnboarded()) return;
    if (!hasSeenTutorial()) setShowTutorial(true);
  }, [isLoggedIn, showOnboarding]);
  // Settings can re-open the tutorial via a window event so we don't need to
  // prop-drill the setter into a lazy-loaded subtree.
  useEffect(() => {
    const open = () => setShowTutorial(true);
    const openTour = () => { setScreen('dashboard'); setShowTour(true); };
    window.addEventListener('bagrut:open-tutorial', open);
    window.addEventListener('bagrut:open-tour', openTour);
    return () => {
      window.removeEventListener('bagrut:open-tutorial', open);
      window.removeEventListener('bagrut:open-tour', openTour);
    };
  }, []);

  const handleNavigate = useCallback((s: AppScreen) => {
    // Clear practice topic filter when navigating away from practice
    if (s !== 'practice') {
      setPracticeTopicId(null);
      setPracticeSearch(null);
    }
    setScreen(s);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const openLesson = useCallback((topicId: string) => {
    setSelectedTopicId(topicId);
    setScreen('lesson_detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const practiceTopic = useCallback((topicId: string) => {
    setPracticeTopicId(topicId);
    setPracticeSearch(null);
    setScreen('practice');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /** Open Practice with the UnitSelect search prefilled — used when navigating
   *  in from a topic name (e.g. "practice my weakest topic" on stats screen). */
  const practiceWithSearch = useCallback((query: string) => {
    setPracticeTopicId(null);
    setPracticeSearch(query);
    setPracticeMistakeQids(null);
    setScreen('practice');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /** Open Practice with a session restricted to specific question IDs.
   *  Used by MistakesReviewModal's "תרגל את הטעויות" CTA. */
  const practiceMistakeBatch = useCallback((qids: string[]) => {
    if (qids.length === 0) return;
    setPracticeTopicId(null);
    setPracticeSearch(null);
    setPracticeMistakeQids(qids);
    setScreen('practice');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginScreen />;
  }

  // First-time user — show onboarding flow before anything else
  if (showOnboarding) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="loading-spinner" /></div>}>
        <OnboardingFlow
          studentName={student?.name}
          onComplete={() => setShowOnboarding(false)}
        />
      </Suspense>
    );
  }

  const lazyFallback = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="loading-spinner" />
    </div>
  );

  const backToDashboard = () => handleNavigate('dashboard');

  // Practice and Exam have their own full-screen layouts (no AppShell nav during active session)
  if (screen === 'practice') {
    return (
      <ScreenErrorBoundary screenName="תרגול" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          <PracticeContainer
            onBack={backToDashboard}
            initialTopicId={practiceTopicId ?? undefined}
            initialSearch={practiceSearch ?? undefined}
            initialQids={practiceMistakeQids ?? undefined}
          />
        </Suspense>
      </ScreenErrorBoundary>
    );
  }

  if (screen === 'weak_practice') {
    return (
      <ScreenErrorBoundary screenName="תרגול חולשות" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          <PracticeContainer onBack={backToDashboard} initialMode="weak" />
        </Suspense>
      </ScreenErrorBoundary>
    );
  }

  if (screen === 'exam') {
    return (
      <ScreenErrorBoundary screenName="בחינה" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          <ExamContainer onBack={backToDashboard} />
        </Suspense>
      </ScreenErrorBoundary>
    );
  }

  if (screen === 'exam_practice') {
    return (
      <ScreenErrorBoundary screenName="תרגול בגרות" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          <ExamPractice onBack={backToDashboard} />
        </Suspense>
      </ScreenErrorBoundary>
    );
  }

  if (screen === 'duel') {
    return (
      <ScreenErrorBoundary screenName="דו-קרב" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          <DuelScreen onBack={backToDashboard} />
        </Suspense>
      </ScreenErrorBoundary>
    );
  }

  // Screens wrapped in AppShell with navigation
  return (
    <AppShell screen={screen} onNavigate={handleNavigate}>
      <ScreenErrorBoundary screenName="דשבורד" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          {screen === 'dashboard' && (
            <Dashboard onNavigate={(s) => handleNavigate(s as AppScreen)} />
          )}
        </Suspense>
      </ScreenErrorBoundary>
      <ScreenErrorBoundary screenName="מסך" onBack={backToDashboard}>
        <Suspense fallback={lazyFallback}>
          {screen === 'achievements' && (
            <AchievementsScreen
              onPracticeTopic={practiceWithSearch}
              onPracticeMistakes={practiceMistakeBatch}
            />
          )}
          {screen === 'leaderboard' && <LeaderboardScreen />}
          {screen === 'goals' && <GoalsScreen />}
          {screen === 'settings' && <SettingsScreen />}
          {screen === 'lessons' && <LessonTopics onSelect={openLesson} />}
          {screen === 'lesson_detail' && selectedTopicId && (
            <Lesson
              topicId={selectedTopicId}
              onBack={() => handleNavigate('lessons')}
              onPractice={() => practiceTopic(selectedTopicId)}
            />
          )}
          {screen === 'learning_path' && (
            <LearningPathScreen
              onBack={backToDashboard}
              onPracticeTopic={(topicId) => practiceTopic(topicId)}
            />
          )}
          {screen === 'admin' && (
            <AdminScreen onBack={backToDashboard} />
          )}
          {screen === 'tutor_dashboard' && (
            <TutorDashboard
              onOpenTutoree={(sid) => { setTutoreeId(sid); setScreen('tutor_overview'); }}
            />
          )}
          {screen === 'tutor_overview' && tutoreeId && (
            <TutorOverview
              studentId={tutoreeId}
              onBack={() => { setTutoreeId(null); setScreen('tutor_dashboard'); }}
            />
          )}
          {screen === 'practice_hub' && (
            <PracticeHub
              onNavigate={handleNavigate}
              onBack={backToDashboard}
              onPracticeTopic={practiceWithSearch}
            />
          )}
          {screen === 'progress' && (
            <ProgressScreen
              onNavigate={handleNavigate}
              onBack={backToDashboard}
              onPracticeMistakes={practiceMistakeBatch}
            />
          )}
        </Suspense>
      </ScreenErrorBoundary>

      {/* Full feature tutorial — lazy, shown once for new users (auto), and on
          demand from Settings via window event. */}
      {showTutorial && (
        <Suspense fallback={null}>
          <TutorialModal
            track={student?.track}
            onClose={() => setShowTutorial(false)}
            onComplete={() => { markTutorialSeen(); setShowTutorial(false); }}
          />
        </Suspense>
      )}

      {/* Guided tour — overlays the real dashboard with spotlight + tooltip.
          Only meaningful on the dashboard screen, so the open handler forces
          a screen='dashboard' switch before mounting. */}
      {showTour && screen === 'dashboard' && (
        <Suspense fallback={null}>
          <GuidedTour
            steps={buildTourSteps(student?.track)}
            onClose={() => setShowTour(false)}
          />
        </Suspense>
      )}
    </AppShell>
  );
}

function AppContent() {
  // ScreenShareHost is a sibling of the screen tree (not nested inside it) so
  // a tutor's "watch my screen" request reaches the student on EVERY screen —
  // including the full-screen practice/exam layouts. It self-gates on role
  // and renders only fixed-position overlays.
  return (
    <>
      <AppMain />
      <ScreenShareHost />
    </>
  );
}

export default function App() {
  // Parent-view route — /parent/<token>. We branch BEFORE AuthProvider so
  // parents never trigger the student login flow. The token IS the auth.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const parentMatch = path.match(/^\/parent\/([^/?#]+)/);
  if (parentMatch) {
    const token = parentMatch[1];
    return (
      <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center" dir="rtl">
            <div className="loading-spinner" />
          </div>
        }>
          <ParentDashboard token={token} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}
